/**
 * CommandPalette — hub-wide search, opened with ⌘K / Ctrl+K, the top-bar search
 * button, or the search row on the mobile More page (Sep 2026).
 *
 * Searches, in this order: the user's open to-dos, every app and sub-page they
 * can open (useAppNav — the same list as the sidebar, so nothing leaks that the
 * sidebar would hide), and people (for users provisioned for the Org Chart and
 * for admins; read once per session from /userPublic — readable by every
 * signed-in user, unlike /users). A person opens their org profile; for an
 * admin it opens the full admin profile instead (Oct 2026).
 *
 * Mounted once inside the signed-in shell (Layout). Open it from anywhere with
 * openCommandPalette().
 */
import { useEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { useNavigate } from 'react-router-dom';
import { collection, getDocs, orderBy, query } from 'firebase/firestore';
import { CornerDownLeft, Search, UserRound, CircleDot, type LucideIcon } from 'lucide-react';
import { db } from '../lib/firebase';
import { Icon } from '../design/Icon';
import { useSharedAppNav, ICONS } from './useAppNav';
import { navDescription } from './navDescriptions';
import { useProfile } from '../profile/ProfileProvider';
import { useVisibleGuideDocs } from '../guide/guide';
import { markUsed } from '../lib/usage';

const OPEN_EVENT = 'daaily-open-command-palette';
export const openCommandPalette = () => window.dispatchEvent(new Event(OPEN_EVENT));

interface Result {
  id: string;
  kind: 'To-do' | 'App' | 'Page' | 'Guide' | 'Person';
  label: string;
  hint?: string;
  to: string;
  icon: LucideIcon;
  /** Extra text matched but not shown (e.g. an email). */
  keywords?: string;
}

interface Person { email: string; displayName?: string; active?: boolean }
let peopleCache: Person[] | null = null;

/** Every query word must appear somewhere; earlier/prefix hits rank higher. */
function score(r: Result, words: string[]): number {
  const hay = `${r.label} ${r.hint ?? ''} ${r.keywords ?? ''}`.toLowerCase();
  let s = 0;
  for (const w of words) {
    const i = hay.indexOf(w);
    if (i < 0) return -1;
    s += i === 0 ? 3 : r.label.toLowerCase().startsWith(w) ? 3 : hay.includes(` ${w}`) ? 2 : 1;
  }
  return s;
}

export const isMac = typeof navigator !== 'undefined' && /Mac|iPhone|iPad/.test(navigator.platform);

export function CommandPalette() {
  const [open, setOpen] = useState(false);
  const [q, setQ] = useState('');
  const [active, setActive] = useState(0);
  const [people, setPeople] = useState<Person[]>(peopleCache ?? []);
  const inputRef = useRef<HTMLInputElement>(null);
  const listRef = useRef<HTMLUListElement>(null);
  const navigate = useNavigate();
  const { state } = useProfile();
  const { navGroups, adminItems, SUB_NAVS, todos, isManager } = useSharedAppNav();
  const isAdmin = state.status === 'ready' && state.isAdmin;
  const canSeePeople = state.status === 'ready' && (state.user.tools?.orgChart?.enabled === true || isAdmin);
  const guideDocs = useVisibleGuideDocs();

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === 'k') {
        e.preventDefault();
        setOpen((o) => !o);
      }
    };
    const onOpen = () => setOpen(true);
    window.addEventListener('keydown', onKey);
    window.addEventListener(OPEN_EVENT, onOpen);
    return () => {
      window.removeEventListener('keydown', onKey);
      window.removeEventListener(OPEN_EVENT, onOpen);
    };
  }, []);

  // Escape closes from anywhere inside the palette; focus returns to where it was.
  const returnFocus = useRef<HTMLElement | null>(null);
  useEffect(() => {
    if (!open) return;
    returnFocus.current = document.activeElement as HTMLElement | null;
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') { e.preventDefault(); setOpen(false); } };
    window.addEventListener('keydown', onKey);
    return () => {
      window.removeEventListener('keydown', onKey);
      returnFocus.current?.focus?.();
    };
  }, [open]);

  useEffect(() => {
    if (!open) return;
    markUsed('palette');
    setQ('');
    setActive(0);
    window.setTimeout(() => inputRef.current?.focus(), 0);
    if (canSeePeople && peopleCache === null) {
      getDocs(query(collection(db, 'userPublic'), orderBy('displayName')))
        .then((snap) => {
          peopleCache = snap.docs.map((d) => d.data() as Person).filter((p) => p.active !== false);
          setPeople(peopleCache);
        })
        .catch(() => { peopleCache = []; });
    }
  }, [open, canSeePeople]);

  const all = useMemo<Result[]>(() => {
    const out: Result[] = [];
    for (const t of todos) out.push({ id: `todo:${t.id}`, kind: 'To-do', label: t.label, hint: t.tool, to: t.link, icon: CircleDot });
    const apps = [
      { to: '/', label: 'Home', icon: ICONS.home },
      ...(isManager ? [{ to: '/my-team', label: 'My Team', icon: ICONS.fte }] : []),
      ...navGroups.flatMap((g) => g.items),
      ...adminItems,
      { to: '/activity', label: 'Activity', icon: CircleDot },
      { to: '/guide', label: 'Guide', icon: ICONS.guide },
      { to: '/profile', label: 'Profile & settings', icon: UserRound },
    ];
    const seen = new Set<string>();
    for (const a of apps) {
      if (seen.has(a.to)) continue;
      seen.add(a.to);
      out.push({ id: `app:${a.to}`, kind: 'App', label: a.label, hint: navDescription(a.to), to: a.to, icon: a.icon });
      for (const c of SUB_NAVS[a.to] ?? []) {
        if (c.to === a.to || seen.has(c.to)) continue;
        seen.add(c.to);
        out.push({ id: `page:${c.to}`, kind: 'Page', label: `${a.label} › ${c.label}`, to: c.to, icon: a.icon });
      }
    }
    // Guide docs: "how does X work" style searches land on the explanation.
    for (const d of guideDocs) {
      out.push({ id: `guide:${d.id}`, kind: 'Guide', label: `${d.title} guide`, hint: d.oneliner, to: `/guide?d=${d.id}`, icon: ICONS.guide, keywords: 'help how does guide explain data' });
    }
    if (canSeePeople) {
      for (const p of people) {
        out.push({
          id: `person:${p.email}`, kind: 'Person', label: p.displayName || p.email,
          hint: isAdmin ? 'Admin profile' : undefined,
          to: isAdmin ? `/admin/users/${encodeURIComponent(p.email)}` : `/org/people/${encodeURIComponent(p.email)}`,
          icon: UserRound, keywords: p.email,
        });
      }
    }
    return out;
  }, [todos, navGroups, adminItems, SUB_NAVS, isManager, people, canSeePeople, isAdmin, guideDocs]);

  const results = useMemo(() => {
    const words = q.toLowerCase().split(/\s+/).filter(Boolean);
    if (words.length === 0) return all.filter((r) => r.kind === 'To-do' || r.kind === 'App').slice(0, 12);
    return all
      .map((r) => ({ r, s: score(r, words) }))
      .filter((x) => x.s >= 0)
      .sort((a, b) => b.s - a.s)
      .slice(0, 30)
      .map((x) => x.r);
  }, [all, q]);

  // Reset on every new result list (typing, or people/to-dos arriving), so Enter never points past it.
  useEffect(() => { setActive(0); }, [results]);
  useEffect(() => {
    listRef.current?.querySelector(`[data-idx="${active}"]`)?.scrollIntoView({ block: 'nearest' });
  }, [active]);

  if (!open) return null;

  const go = (r: Result | undefined) => {
    if (!r) return;
    setOpen(false);
    navigate(r.to);
  };

  return createPortal(
    <div
      className="fixed inset-0 z-modal bg-app/70 backdrop-blur-sm flex items-start justify-center p-4 pt-[12vh]"
      onMouseDown={(e) => { if (e.target === e.currentTarget) setOpen(false); }}
      role="dialog"
      aria-modal="true"
      aria-label="Search the hub"
    >
      <div className="w-full max-w-xl rounded-xl border border-line bg-surface text-fg shadow-lg overflow-hidden">
        <div className="flex items-center gap-2 px-4 border-b border-line">
          <Icon as={Search} className="text-fg-faint" />
          <input
            ref={inputRef}
            value={q}
            onChange={(e) => setQ(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'ArrowDown') { e.preventDefault(); setActive((a) => Math.min(a + 1, results.length - 1)); }
              else if (e.key === 'ArrowUp') { e.preventDefault(); setActive((a) => Math.max(a - 1, 0)); }
              else if (e.key === 'Enter') { e.preventDefault(); go(results[Math.min(active, results.length - 1)]); }
            }}
            placeholder={canSeePeople ? 'Search apps, to-dos and people' : 'Search apps and to-dos'}
            className="flex-1 h-12 !border-0 !bg-transparent !ring-0 !shadow-none px-0 text-base focus:!outline-none"
            aria-controls="cmdk-results"
          />
          <kbd className="hidden sm:inline text-2xs text-fg-faint border border-line rounded px-1.5 py-0.5">esc</kbd>
        </div>
        <ul id="cmdk-results" ref={listRef} role="listbox" className="max-h-[40vh] sm:max-h-[55vh] overflow-y-auto py-1">
          {results.length === 0 && <li className="px-4 py-8 text-center text-sm text-fg-muted">Nothing matches &ldquo;{q}&rdquo;.</li>}
          {results.map((r, i) => (
            <li key={r.id} data-idx={i} role="option" aria-selected={i === active}>
              <button
                type="button"
                onMouseMove={() => setActive(i)}
                onClick={() => go(r)}
                className={`w-full flex items-center gap-3 px-4 py-2 text-left ${i === active ? 'bg-surface-2' : ''}`}
              >
                <Icon as={r.icon} className={r.kind === 'To-do' ? 'text-brand' : 'text-fg-muted'} />
                <span className="min-w-0 flex-1">
                  <span className="block text-sm truncate">{r.label}</span>
                  {r.hint && <span className="block text-xs text-fg-muted truncate">{r.hint}</span>}
                </span>
                <span className="text-2xs text-fg-faint shrink-0">{r.kind}</span>
                {i === active && <Icon as={CornerDownLeft} size={14} className="text-fg-faint" />}
              </button>
            </li>
          ))}
        </ul>
      </div>
    </div>,
    document.body,
  );
}

/** Top-bar trigger: a search field look-alike on desktop, an icon on phones. */
export function SearchButton() {
  return (
    <>
      <button
        type="button"
        onClick={openCommandPalette}
        className="hidden md:inline-flex items-center gap-2 h-8 w-56 px-3 rounded-md border border-line bg-surface text-sm text-fg-faint hover:border-line-strong transition-colors duration-fast"
      >
        <Icon as={Search} />
        <span className="flex-1 text-left">Search</span>
        <kbd className="text-2xs border border-line rounded px-1.5">{isMac ? '⌘K' : 'Ctrl K'}</kbd>
      </button>
      <button
        type="button"
        onClick={openCommandPalette}
        aria-label="Search"
        className="md:hidden text-fg-muted hover:text-fg transition-colors duration-fast"
      >
        <Icon as={Search} size={20} />
      </button>
    </>
  );
}
